import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/i18n";
import Icon from "@/components/shared/Icon";

// Shared multiple-choice engine for quiz-style games.
// questions: [{ promptNode?, prompt, options:[], answer:number }]
export default function QuizEngine({ questions, onFinish }) {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);

  const qn = questions[idx];
  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === qn.answer) setScore((s) => s + 1);
  };
  const next = () => {
    const correct = picked === qn.answer;
    if (idx + 1 >= questions.length) {
      onFinish(score + 0, questions.length); // score already includes current
      return;
    }
    setIdx((v) => v + 1);
    setPicked(null);
    void correct;
  };

  return (
    <div>
      <div className="flex items-center justify-between text-sm font-bold text-[#64748B]">
        <span data-testid="quiz-progress">{t("play.question")} {idx + 1} {t("play.of")} {questions.length}</span>
        <span className="inline-flex items-center gap-1.5 text-[#147DEB]"><Icon name="Star" className="h-4 w-4 text-[#FFC928]" /> {t("common.score")}: {score}</span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#E8F5FF]">
        <div className="h-full rounded-full bg-[#147DEB] transition-all duration-300" style={{ width: `${((idx) / questions.length) * 100}%` }} />
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.25 }} className="mt-6">
          {qn.promptNode}
          {qn.prompt && <p className="font-display text-center text-2xl font-bold text-[#082B66]">{qn.prompt}</p>}
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {qn.options.map((opt, i) => {
              const show = picked !== null;
              const isCorrect = i === qn.answer;
              return (
                <button
                  key={i}
                  onClick={() => pick(i)}
                  data-testid={`quiz-option-${i}`}
                  className={`rounded-2xl border-2 px-5 py-4 text-lg font-bold transition-all ${
                    show && isCorrect
                      ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                      : show && picked === i
                      ? "border-rose-300 bg-rose-50 text-rose-700"
                      : "border-[#147DEB]/15 bg-white text-[#082B66] hover:-translate-y-0.5 hover:border-[#147DEB]/50"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          {picked !== null && (
            <div className="mt-5 flex items-center justify-between">
              <span className={`font-bold ${picked === qn.answer ? "text-emerald-600" : "text-rose-500"}`} data-testid="quiz-feedback">
                {picked === qn.answer ? t("common.correct") : t("common.incorrect")}
              </span>
              <button onClick={next} data-testid="quiz-next-button" className="inline-flex items-center gap-1.5 rounded-2xl bg-[#147DEB] px-6 py-2.5 font-bold text-white hover:bg-[#0F62BD]">
                {idx + 1 >= questions.length ? t("play.summary") : t("common.next")} <Icon name="ArrowRight" className="h-4 w-4" />
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
